import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { Transaction } from 'types';

import operationTypes from 'constants/operationTypes';

import { getOperation } from 'modules/transactions/redux/operationActions';
import { CustomOperation } from '../types/CustomOperation';

type UseGetOperationProps = {
  operationId: string | undefined;
};

const checkIsOldOperation = (operation: CustomOperation): boolean => {
  const transactions = operation.transactions || [];
  const transaction: Transaction = transactions?.[0] as Transaction;

  const isOldReclassification =
    !!transaction?.partner &&
    !transaction?.newShareFrom &&
    !transaction?.newShareTo;

  return (
    !!operation?.contributions?.length ||
    (!!operation?.transactions?.length &&
      operation.operationType === operationTypes.SPLIT) ||
    isOldReclassification
  );
};

const checkIsIndependentOperation = (operation: CustomOperation): boolean =>
  operation.operationType === operationTypes.DIVIDENDS_DISTRIBUTION ||
  operation.operationType === operationTypes.CONTRIBUTION ||
  operation.operationType === operationTypes.SPLIT;

export const useGetOperation = ({ operationId }: UseGetOperationProps) => {
  const [operation, setOperation] = useState<CustomOperation>();
  const [isOldOperation, setIsOldOperation] = useState<boolean>(false);
  const [isIndependentOperation, setIsIndependentOperation] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchOperation = useCallback(async () => {
    if (!operationId) return;

    setIsLoading(true);

    try {
      const operation = await store.dispatch(
        getOperation(operationId, {
          fieldsToPopulate: [
            'transactions.partner',
            'transactions.shareClass',
            'transactions.newShareClass',
            'dividends.partner',
            'contributions.partner',
            'movements.partner',
            'movements.shareClass',
          ],
        })
      );

      const isOldOperation = checkIsOldOperation(operation as CustomOperation);

      const isIndependentOperation = checkIsIndependentOperation(
        operation as CustomOperation
      );

      setOperation(operation);
      setIsOldOperation(isOldOperation);
      setIsIndependentOperation(isIndependentOperation);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [operationId]);

  useEffect(() => {
    fetchOperation();
  }, [fetchOperation]);

  return {
    data: { operation, isOldOperation, isIndependentOperation },
    isLoading,
  };
};
